import React, { useState, useEffect } from 'react';
import { Bell, Mail, Music, BookOpen, Sparkles, X, Save } from 'lucide-react';
import { notificationService } from '../../../services/notificationService';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';

export default function NotificationPreferences({ isOpen, onClose, userId }) {
  const [preferences, setPreferences] = useState({
    newSongsEnabled: true,
    newLessonsEnabled: true,
    newContentEnabled: true,
    emailEnabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      loadPreferences();
    }
  }, [isOpen, userId]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError('');
      const prefs = await notificationService?.getPreferences(userId);
      setPreferences(prefs);
    } catch (err) {
      console.error('Error loading preferences:', err);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess(false);
      
      await notificationService?.updatePreferences(userId, preferences);
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm p-6 border-b border-white border-opacity-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-yellow-400" />
            <h2 className="text-2xl font-bold text-white">Notification Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 bg-red-500/10 border border-red-500 rounded-xl p-4">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="mb-4 bg-green-500/10 border border-green-500 rounded-xl p-4">
                  <p className="text-green-200 text-sm">Preferences saved successfully!</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Master Email Toggle */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <Mail className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-white font-semibold">Email Notifications</h3>
                        <p className="text-white/70 text-sm mt-1">
                          Receive email notifications about your favorite musicals
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggle('emailEnabled')}
                      className={`relative w-14 h-7 rounded-full transition-colors ${
                        preferences?.emailEnabled ? 'bg-purple-600' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                          preferences?.emailEnabled ? 'translate-x-7' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Individual Notification Types */}
                <div className={`space-y-3 transition-opacity ${preferences?.emailEnabled ? 'opacity-100' : 'opacity-50'}`}>
                  {/* New Songs */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <Music className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="text-white font-medium">New Songs</h3>
                          <p className="text-white/70 text-sm">
                            Get notified when new songs are added to your favorites
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggle('newSongsEnabled')}
                        disabled={!preferences?.emailEnabled}
                        className={`relative w-14 h-7 rounded-full transition-colors ${
                          preferences?.newSongsEnabled && preferences?.emailEnabled 
                            ? 'bg-purple-600' :'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                            preferences?.newSongsEnabled && preferences?.emailEnabled 
                              ? 'translate-x-7' :'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* New Lessons */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <BookOpen className="w-5 h-5 text-orange-400 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="text-white font-medium">New Lessons</h3>
                          <p className="text-white/70 text-sm">
                            Be alerted when new lessons become available
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggle('newLessonsEnabled')}
                        disabled={!preferences?.emailEnabled}
                        className={`relative w-14 h-7 rounded-full transition-colors ${
                          preferences?.newLessonsEnabled && preferences?.emailEnabled 
                            ? 'bg-purple-600' :'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                            preferences?.newLessonsEnabled && preferences?.emailEnabled 
                              ? 'translate-x-7' :'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* New Content */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <Sparkles className="w-5 h-5 text-pink-400 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="text-white font-medium">New Content</h3>
                          <p className="text-white/70 text-sm">
                            Stay updated on all new content and features
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggle('newContentEnabled')}
                        disabled={!preferences?.emailEnabled}
                        className={`relative w-14 h-7 rounded-full transition-colors ${
                          preferences?.newContentEnabled && preferences?.emailEnabled 
                            ? 'bg-purple-600' :'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                            preferences?.newContentEnabled && preferences?.emailEnabled 
                              ? 'translate-x-7' :'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm p-6 border-t border-white border-opacity-20">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <LoadingSpinner />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Save Preferences</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}